import { SupplierStatusDto } from "../responses/SupplierStatusDto";
import {
  SUPPLIER_OPS,
  SupplierOp,
  SupplierStatus,
  SupplierStatusReason,
} from "../../../core/models/entities/SupplierStatus";

// Every field is read defensively: the banner must never crash on an older
// API that omits a field or on a value of the wrong type - an unreadable
// answer degrades to "ok" (banner hidden), never to "down".

// An ISO date-time WITHOUT a zone designator ("2026-09-14T19:47:00",
// "…T19:47:00.1234567"): a .NET DateTime of Kind Unspecified round-tripped
// through Redis/JSON. The API's timestamps are UTC by contract, but the
// browser reads such a string as LOCAL time (2 h off in DK: `since` in the
// future, no "Started …" text, an unreliable episode key), so the zone is
// pinned to UTC before parsing. A Z or an explicit offset is left untouched.
const ISO_DATE_TIME_WITHOUT_ZONE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;

function parseUtc(value: unknown): Date | null {
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (text === "") return null;
  const date = new Date(
    ISO_DATE_TIME_WITHOUT_ZONE.test(text) ? `${text}Z` : text
  );
  return Number.isNaN(date.getTime()) ? null : date;
}

function parseReason(value: unknown): SupplierStatusReason | null {
  return value === "latchOpen" || value === "probeFailed" ? value : null;
}

function parseAffected(value: unknown): SupplierOp[] {
  if (!Array.isArray(value)) return [];
  return value.filter((op): op is SupplierOp =>
    (SUPPLIER_OPS as readonly string[]).includes(op)
  );
}

function parseRetryAfterSeconds(value: unknown): number {
  const seconds = Number(value);
  return Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : 0;
}

/** GET supplier-status -> SupplierStatus. `now` injectable for tests. */
export function mapSupplierStatus(
  dto: Partial<SupplierStatusDto> | null | undefined,
  now: number = Date.now()
): SupplierStatus {
  const degraded = dto?.degraded === true;
  return {
    ok: dto?.ok === true || (dto?.ok === undefined && !degraded),
    degraded,
    reason: degraded ? parseReason(dto?.reason) : null,
    since: degraded ? parseUtc(dto?.since) : null,
    retryAfterSeconds: parseRetryAfterSeconds(dto?.retryAfterSeconds),
    affected: degraded ? parseAffected(dto?.affected) : [],
    lastProbeUtc: parseUtc(dto?.lastProbeUtc),
    lastProbeOk:
      typeof dto?.lastProbeOk === "boolean" ? dto.lastProbeOk : null,
    checkedUtc: parseUtc(dto?.checkedUtc) ?? new Date(now),
    routeMissing: false,
  };
}

/**
 * The status while the route answers 404 (API flag off / not deployed): a
 * silent "ok" that the banner never shows. `now` injectable for tests.
 */
export function supplierStatusRouteMissing(now: number = Date.now()): SupplierStatus {
  return {
    ok: true,
    degraded: false,
    reason: null,
    since: null,
    retryAfterSeconds: 0,
    affected: [],
    lastProbeUtc: null,
    lastProbeOk: null,
    checkedUtc: new Date(now),
    routeMissing: true,
  };
}
